/**
 * Модель вкладок Heroes / Level / Stars / Tech (C5): чистая функция «баланс + игрок → строки с ценами и причинами
 * отказа». Цены и правила — из engine (`canPurchase`, `heroLevelCost`, `nextStar`, `techLevelCost`, `heroDamage`),
 * своих формул нет: клиент и сервер (S3) считают одинаково.
 */
import type { BalanceData, HeroId, PlayerState, PurchaseRequest } from "@newfungame/contracts";
import { TICK_RATE } from "@newfungame/contracts";
import { canPurchase, heroDef, heroLevelCost, heroPower, nextStar, techLevel, techLevelCost } from "@newfungame/engine";

/**
 * Почему покупка недоступна: герой закрыт / нужен уровень героя (звезда) / максимум / не хватает монет / сервер только
 * что ответил «не хватает монет» (до следующего зачисления, ISSUE-149).
 */
export type BlockReason = "heroLocked" | "needLevel" | "max" | "coins" | "short";

export type Buy =
  | { ok: true; cost: number }
  /** `need`: heroLocked — волна открытия; needLevel — требуемый уровень; coins — сколько монет не хватает. */
  | { ok: false; reason: BlockReason; cost: number | null; need: number };

export interface HeroRow {
  id: HeroId;
  unlocked: boolean;
  unlockWave: number;
  level: number;
  maxLevel: number;
  stars: number;
  /** Сила (урон в секунду по одной цели) с уровнем и звёздами. */
  power: number;
  skillName: string;
  skillDescription: string;
  /** Перезарядка навыка, секунд. */
  skillCooldown: number;
}

export interface LevelRow {
  id: HeroId;
  level: number;
  maxLevel: number;
  /** Урон в секунду по одной цели (`heroPower`: точный — целый урон за уровень округляется вниз и росту не виден). */
  power: number;
  /** Он же после покупки; `null` — максимум. */
  nextPower: number | null;
  buy: Buy;
  req: PurchaseRequest;
}

export interface StarRow {
  id: HeroId;
  level: number;
  stars: number;
  /** Следующая звезда: номер, цена, рубеж уровня и эффект; `null` — 5★. */
  next: { star: number; cost: number; requiredLevel: number; damagePct: number; cooldownCut: boolean } | null;
  /** Рубежи уровня по звёздам 1…5 и какие уже взяты. */
  milestones: { star: number; level: number; owned: boolean }[];
  buy: Buy;
  req: PurchaseRequest;
}

export interface TechRow {
  id: string;
  name: string;
  description: string;
  level: number;
  maxLevel: number;
  buy: Buy;
  req: PurchaseRequest;
}

export interface TabsModel {
  /** Монеты, которые сервер уже зачислил: по ним и считаются покупки. */
  coins: number;
  /** Монеты в пути: текущая волна и волны, чей журнал сервер ещё не принял. Показываются отдельно «+N». */
  incoming: number;
  heroes: HeroRow[];
  level: LevelRow[];
  stars: StarRow[];
  tech: TechRow[];
  /** Есть ли что купить прямо сейчас — красная точка на вкладке. */
  affordable: { level: boolean; stars: boolean; tech: boolean };
}

/** Звёзды, на которых навык ещё и перезаряжается быстрее (content-v1: 3★ и 5★). */
export const COOLDOWN_STARS: readonly number[] = [3, 5];

function evaluateBuy(b: BalanceData, p: PlayerState, req: PurchaseRequest, cost: number | null): Buy {
  const c = canPurchase(b, p, req);
  if (c.ok) return { ok: true, cost: c.cost };
  switch (c.code) {
    case "insufficient_coins":
      return { ok: false, reason: "coins", cost, need: Math.max(0, (cost ?? 0) - p.coins) };
    case "locked": {
      if (req.kind === "heroStar") {
        const hero = p.heroes.find((h) => h.heroId === req.heroId);
        if (hero?.unlocked)
          return { ok: false, reason: "needLevel", cost, need: nextStar(b, hero.stars)?.requiredLevel ?? 0 };
      }
      const id = "heroId" in req ? req.heroId : null;
      return { ok: false, reason: "heroLocked", cost, need: id ? heroDef(b, id).unlockWave : 0 };
    }
    default:
      return { ok: false, reason: "max", cost: null, need: 0 };
  }
}

/** Кошелёк для вкладок (ISSUE-149): что сервер уже зачислил, что в пути и был ли отказ «не хватает монет». */
export interface TabsWallet {
  /** Монеты текущей волны (снимок боя). */
  earnedThisWave?: number;
  /** Монеты волн, уже прибавленные к `p.coins`, но ещё не принятые сервером. */
  inFlight?: number;
  /** Сервер ответил «не хватает монет», зачислений с тех пор не было: все покупки гаснут. */
  short?: boolean;
}

/**
 * Покупки считают только по монетам, которые сервер уже зачислил: `p.coins` без монет в пути. Монеты текущей волны и
 * волн, чей журнал ещё не принят, в шапке листа — отдельным «+N»: потратить их раньше зачисления нельзя.
 */
export function toTabsModel(b: BalanceData, player: PlayerState, wallet: TabsWallet = {}): TabsModel {
  const inFlight = Math.min(player.coins, wallet.inFlight ?? 0);
  const p: PlayerState = inFlight > 0 ? { ...player, coins: player.coins - inFlight } : player;
  const short = wallet.short ?? false;
  const evaluate = (...args: Parameters<typeof evaluateBuy>): Buy => {
    const buy = evaluateBuy(...args);
    return short && buy.ok ? { ok: false, reason: "short", cost: buy.cost, need: 0 } : buy;
  };
  const heroes: HeroRow[] = [];
  const level: LevelRow[] = [];
  const stars: StarRow[] = [];
  for (const prog of p.heroes) {
    const def = heroDef(b, prog.heroId);
    const skill = b.skills.find((s) => s.id === def.skillId);
    heroes.push({
      id: prog.heroId,
      unlocked: prog.unlocked,
      unlockWave: def.unlockWave,
      level: prog.level,
      maxLevel: def.maxLevel,
      stars: prog.stars,
      power: heroPower(b, prog.heroId, prog.level, prog.stars),
      skillName: skill?.name ?? "",
      skillDescription: skill?.description ?? "",
      skillCooldown: skill ? Math.round(skill.cooldownTicks / TICK_RATE) : 0,
    });

    const lvlCost = heroLevelCost(b, prog.heroId, prog.level);
    const lvlReq: PurchaseRequest = { kind: "heroLevel", heroId: prog.heroId };
    level.push({
      id: prog.heroId,
      level: prog.level,
      maxLevel: def.maxLevel,
      power: heroPower(b, prog.heroId, prog.level, prog.stars),
      nextPower: lvlCost === null ? null : heroPower(b, prog.heroId, prog.level + 1, prog.stars),
      buy: evaluate(b, p, lvlReq, lvlCost),
      req: lvlReq,
    });

    const star = nextStar(b, prog.stars);
    const starReq: PurchaseRequest = { kind: "heroStar", heroId: prog.heroId };
    stars.push({
      id: prog.heroId,
      level: prog.level,
      stars: prog.stars,
      next: star && {
        ...star,
        damagePct: b.economy.stars.damagePerStarPermille / 10,
        cooldownCut: COOLDOWN_STARS.includes(star.star),
      },
      milestones: b.economy.stars.levels.map((lv, i) => ({ star: i + 1, level: lv, owned: prog.stars > i })),
      buy: evaluate(b, p, starReq, star?.cost ?? null),
      req: starReq,
    });
  }

  const tech: TechRow[] = b.tech.map((t) => {
    const lv = techLevel(t, p.tech);
    const req: PurchaseRequest = { kind: "tech", techId: t.id };
    return {
      id: t.id,
      name: t.name,
      description: t.description,
      level: lv,
      maxLevel: t.maxLevel,
      buy: evaluate(b, p, req, techLevelCost(b, t.id, lv)),
      req,
    };
  });

  const any = (rows: { buy: Buy }[]) => rows.some((r) => r.buy.ok);
  return {
    coins: p.coins,
    incoming: inFlight + (wallet.earnedThisWave ?? 0),
    heroes,
    level,
    stars,
    tech,
    affordable: { level: any(level), stars: any(stars), tech: any(tech) },
  };
}
